/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useCallback, useEffect, useState} from 'react';
import { Drawer} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import styles from './UserList.module.scss';
import {User} from "../../types/User";
import SimplePaging from "../../components/common/paging/SimplePaging";
import PageTitle from "../../components/common/PageTitle/PageTitle";
import {emptyUserListFilter, UserListFilterIF} from "./UserList.interface";
import UserListPanel from "./UserListPanel/UserListPanel";
import userAPI from "../../api/userAPI";
import UserListFilters from "./UserListFilters/UserListFilters";
import apiClient from "../../api/apiClient";
import {useMessageStore} from "../../stores/messageStore";
import UserListTable from "./UserList/UserListTable";
import {useAppStore} from "../../stores/appStore";

const UserList = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const setMessages = useMessageStore((state) => state.setMessages);
	const setShouldShowSpinner = useAppStore((state) => state.setShouldShowSpinner);
	const [list, setList] = useState<User[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [selectedItem, setSelectedItem] = useState<User | undefined>(undefined);
	const [filter, setFilter] = useState<UserListFilterIF>(emptyUserListFilter);
	const [shouldUserListPanelRerender, setShouldUserListPanelRerender] = useState(false);
	const setFilterCb = useCallback(setFilter, []);

	const fetchUserUntilFound = useCallback(async (targetId: string, startPage = 1) => {
		setShouldUserListPanelRerender(true);
		let page = startPage;

		while (true) {
			const res = await userAPI.fetchUserListAPI({ ...filter, page });
			if (res.length === 0) {
				console.warn("해당 ID를 가진 유저를 찾을 수 없습니다.");
				break;
			}

			const found = res.find((item: User) => item.id === targetId);
			if (found) {
				setList(res);
				setCurrentPage(page);
				setSelectedItem(found);
				navigate('/app/business/userList', { replace: true });
				break;
			}

			page += 1;
		}
		setShouldUserListPanelRerender(false);
	}, []);

	useEffect(() => {
		userAPI.fetchUserListAPI({...filter, page: currentPage})
			.then((res) => {
				setList(res);
			})
	}, []);

	useEffect(() => {
		if (id) {
			userAPI.fetchUserListAPI({ ...filter, page: currentPage, id })
				.then((res) => {
					setList(res);
					const userData = res[0];
					setSelectedItem(userData);
					navigate('/app/business/userList', { replace: true });
					const isBusinessUnderReview = userData.businessStatus === 'review';
					const isCarUnderReview = userData.reviewCount && Number(userData.reviewCount) > 0;
					setFilter((prev) => ({ ...prev, isVerified: !!(isBusinessUnderReview || isCarUnderReview) }));
				})
		}
	}, [id]);

	const changeCurrentPage = (value: number) => {
		const nextPage = Math.max(currentPage + value, 1);
		setCurrentPage(nextPage);
		userAPI.fetchUserListAPI({...filter, page: nextPage})
			.then((res) => {
				setList(res);
			});
	}

	const onClickRow = async (data: User) => {
		const res = await userAPI.fetchUserListAPI({ ...filter, page: currentPage });
		const freshItem = res.find((item) => item.id === data.id);

		if (freshItem) {
			setList(res);
			setSelectedItem(freshItem);
		} else {
			console.warn('선택한 유저를 찾을 수 없습니다.');
		}
	};

	const onSearch = () => {
		userAPI.fetchUserListAPI({...filter, page: 1})
			.then((res) => {
				setList(res);
			});
		setCurrentPage(1);
	};

	const onClearFilter = () => {
		userAPI.fetchUserListAPI({...emptyUserListFilter, page: 1})
			.then((res) => {
				setList(res);
			});
		setFilter(emptyUserListFilter);
		setCurrentPage(1);
	};

	const verifyAll = async () => {
		setShouldShowSpinner(true);
		try {
			if (!selectedItem?.id) return;
			await apiClient.put(`/api/admin/business/user/verifyAll`, {
				targetUserId: selectedItem.id,
				status: 'verified',
			});
			setMessages('success', `일괄 인증 완료: ${selectedItem.name}`);

			fetchAgain();
		} catch (e) {
			console.error(e);
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const fetchAgain = async () => {
		if (selectedItem?.id) {
			await fetchUserUntilFound(selectedItem.id);
		}
	};

	return (
		<div className={styles.wrap}>
			{/* 타이틀 */}
			<PageTitle title="회원 조회"/>

			{/* 필터 영역 */}
			<div className={styles.filterWrap}>
				<UserListFilters filter={filter} setFilter={setFilterCb} onSearch={onSearch} onClearFilter={onClearFilter} />
			</div>

			{/* 표 영역 */}
			<div>
				<UserListTable
					list={list}
					onClickRow={onClickRow}
				/>
			</div>

			<div style={{
				display: 'flex',
				justifyContent: 'center',
				marginTop: '16px'
			}}>
				<SimplePaging
					currentPage={currentPage}
					dataLength={list.length}
					onClickPrePageBtn={() => changeCurrentPage(-1)}
					onClickNextPageBtn={() => changeCurrentPage(1)}
				/>
			</div>

			<div>
				<Drawer
					width={1200}
					title={selectedItem?.name || '(이름 없음)'}
					open={selectedItem !== undefined}
					onClose={() => setSelectedItem(undefined)}
					destroyOnClose
				>
					<UserListPanel
						shouldUserListPanelRerender={shouldUserListPanelRerender}
						selectedItem={selectedItem}
						afterSubmit={fetchAgain}
						verifyAll={verifyAll}
					/>
				</Drawer>
			</div>
		</div>
	);
};

export default UserList;
